import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { API_BASE } from "../../utils/api";

// Polyfill for HTML5 Drag and Drop on touch devices
import "drag-drop-touch";

const ManageDisplayOrderModal = ({ isOpen, onClose, onSuccess }) => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const containerRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      fetchCategories();
    }
  }, [isOpen]);

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/api/categories/all`);
      const sorted = res.data.sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));
      setCategories(sorted);
    } catch (err) {
      toast.error("Failed to fetch categories");
    } finally {
      setLoading(false);
    }
  };

  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.setData("dragIndex", index.toString());
    e.dataTransfer.effectAllowed = "move";
    
    // Set drag image to the whole row for better visual feedback
    if (e.dataTransfer.setDragImage) {
      const row = e.currentTarget.closest('.category-row');
      if (row) {
        // Offset so it feels natural under the finger/cursor
        e.dataTransfer.setDragImage(row, 30, row.offsetHeight / 2);
      }
    }
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  const handleDragOverItem = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDragOverContainer = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";

    const container = containerRef.current;
    if (!container) return;

    const scrollSpeed = 15;
    const scrollThreshold = 80;

    const rect = container.getBoundingClientRect();
    // Calculate Y position relative to container
    const y = e.clientY - rect.top;

    // Check if we are close to the top edge
    if (y < scrollThreshold) {
      container.scrollTop -= scrollSpeed;
    }
    // Check if we are close to the bottom edge
    else if (rect.height - y < scrollThreshold) {
      container.scrollTop += scrollSpeed;
    }
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    e.stopPropagation(); // Prevent container drop from firing if item drop fires
    const dragIndexStr = e.dataTransfer.getData("dragIndex");
    let dragIndex = draggedIndex;

    // Fallback to dataTransfer if state is lost, common in some browsers
    if (dragIndex === null && dragIndexStr !== "") {
      dragIndex = parseInt(dragIndexStr, 10);
    }

    if (dragIndex === null || isNaN(dragIndex)) return;
    if (dragIndex === dropIndex) {
      setDraggedIndex(null);
      return;
    }

    const newCats = [...categories];
    const draggedItem = newCats[dragIndex];

    newCats.splice(dragIndex, 1);
    newCats.splice(dropIndex, 0, draggedItem);

    const updatedCats = newCats.map((cat, idx) => ({ ...cat, displayOrder: idx + 1 }));
    setCategories(updatedCats);
    setDraggedIndex(null);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = categories.map(cat => ({
        id: cat._id,
        displayOrder: cat.displayOrder
      }));
      const token = localStorage.getItem("token") || "";
      await axios.post(
        `${API_BASE}/api/categories/update-display-order`,
        { categories: payload },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Update display order successfully");
      onSuccess?.();
      onClose();
    } catch (err) {
      console.error(err);
      toast.error("Failed to save display order");
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[90vh]">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between bg-gray-50/80">
          <h3 className="text-xl font-bold text-gray-800">Manage Display Order</h3>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div
          className="p-6 overflow-y-auto flex-1 bg-gray-50/30 pb-20"
          ref={containerRef}
          onDragOver={handleDragOverContainer}
          onDragEnter={handleDragOverContainer}
          onDrop={(e) => handleDrop(e, categories.length - 1)}
        >
          {loading ? (
            <div className="flex justify-center items-center py-10">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <div className="space-y-2">
              <div className="flex gap-4 px-4 py-2 font-semibold text-gray-600 text-sm border-b border-gray-200">
                <div className="w-16">Order</div>
                <div className="flex-1">Category Name</div>
              </div>
              {categories.map((cat, index) => (
                <div
                  key={cat._id}
                  className={`category-row flex items-center gap-4 px-4 py-3 bg-white border rounded-lg shadow-sm transition-all group ${
                    draggedIndex === index 
                      ? "opacity-40 border-blue-400 bg-blue-50 ring-2 ring-blue-100" 
                      : "border-gray-200 hover:border-blue-300 hover:shadow-md"
                  }`}
                  onDragOver={handleDragOverItem}
                  onDragEnter={handleDragOverItem}
                  onDrop={(e) => handleDrop(e, index)}
                >
                  <div 
                    className="flex items-center gap-3 flex-1"
                    // We allow dropping on the text area too
                  >
                    <div
                      draggable
                      onDragStart={(e) => handleDragStart(e, index)}
                      onDragEnd={handleDragEnd}
                      className="w-12 h-10 flex items-center gap-2 text-gray-500 font-medium cursor-grab active:cursor-grabbing touch-none select-none hover:bg-gray-50 rounded-md px-2"
                      title="Drag to reorder"
                    >
                      <svg className="w-5 h-5 text-gray-400 group-hover:text-blue-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
                      </svg>
                      <span className="text-sm">{cat.displayOrder}</span>
                    </div>
                    
                    <div className="flex-1 font-medium text-gray-800 select-none">
                      {cat.name?.en || "Unknown"}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:ring-4 focus:ring-gray-100 transition-all"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || loading}
            className="px-5 py-2.5 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 focus:ring-4 focus:ring-blue-200 disabled:opacity-50 flex items-center gap-2 transition-all"
          >
            {saving && (
              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
            )}
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
};

export default ManageDisplayOrderModal;
